import React, { useState, useRef, useEffect } from 'react';

interface EstimateItem {
  id?: number;
  item_name: string;
  specification: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
  notes: string;
  sort_order: number;
}

interface ItemEditorProps {
  items: EstimateItem[];
  onChange: (items: EstimateItem[]) => void;
}

const ItemEditor: React.FC<ItemEditorProps> = ({ items, onChange }) => {
  const [itemMasterCache, setItemMasterCache] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<{ [key: number]: string }>({});
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // 品目マスタ読み込み
  useEffect(() => {
    const fetchItemMaster = async () => {
      try {
        const result = await window.api.item_master.list({});
        setItemMasterCache(result);
      } catch (err) {
        console.error('品目マスタ取得エラー:', err);
      }
    };
    fetchItemMaster();
  }, []);

  const addRow = () => {
    const newItem: EstimateItem = {
      item_name: '',
      specification: '',
      quantity: 1,
      unit: '',
      unit_price: 0,
      amount: 0,
      notes: '',
      sort_order: items.length,
    };
    onChange([...items, newItem]);
  };

  const removeRow = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems.map((item, i) => ({ ...item, sort_order: i })));
  };

  const moveRow = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    onChange(newItems.map((item, i) => ({ ...item, sort_order: i })));
  };

  const updateItem = (index: number, field: keyof EstimateItem, value: any) => {
    const newItems = items.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };

      // 金額自動計算
      if (field === 'quantity' || field === 'unit_price') {
        updated.amount = (updated.quantity || 0) * (updated.unit_price || 0);
      }

      return updated;
    });
    onChange(newItems);
  };

  const handleItemSelect = (index: number, masterItem: any) => {
    const newItems = items.map((item, i) => {
      if (i !== index) return item;
      return {
        ...item,
        item_name: masterItem.name,
        specification: masterItem.specification || '',
        unit: masterItem.unit || '',
        unit_price: masterItem.unit_price || 0,
        amount: (item.quantity || 0) * (masterItem.unit_price || 0),
      };
    });
    onChange(newItems);
    setShowDropdown(null);
    setSearchText(prev => ({ ...prev, [index]: masterItem.name }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const fields = ['item_name', 'specification', 'quantity', 'unit', 'unit_price', 'notes'];
      const currentFieldIndex = fields.indexOf(field);
      if (currentFieldIndex < fields.length - 1) {
        const nextField = fields[currentFieldIndex + 1];
        const nextInput = inputRefs.current[`${index}-${nextField}`];
        if (nextInput) {
          nextInput.focus();
        }
      } else {
        // 最終フィールドなら次の行へ
        const nextRowFirstInput = inputRefs.current[`${index + 1}-item_name`];
        if (nextRowFirstInput) {
          nextRowFirstInput.focus();
        } else {
          addRow();
        }
      }
    }
  };

  const filteredItems = (index: number) => {
    const search = searchText[index] || '';
    if (!search) return itemMasterCache.slice(0, 10);
    return itemMasterCache.filter(
      (item: any) =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        (item.code && item.code.toLowerCase().includes(search.toLowerCase()))
    ).slice(0, 10);
  };

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '8px', width: '30px' }}></th>
            <th style={{ padding: '8px', textAlign: 'left', minWidth: '150px' }}>項目名</th>
            <th style={{ padding: '8px', textAlign: 'left', minWidth: '120px' }}>仕様</th>
            <th style={{ padding: '8px', textAlign: 'right', width: '70px' }}>数量</th>
            <th style={{ padding: '8px', textAlign: 'left', width: '60px' }}>単位</th>
            <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>単価</th>
            <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>金額</th>
            <th style={{ padding: '8px', textAlign: 'left', minWidth: '100px' }}>備考</th>
            <th style={{ padding: '8px', width: '40px' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
              {/* 並び替え */}
              <td style={{ padding: '4px', textAlign: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <button
                    onClick={() => moveRow(index, 'up')}
                    disabled={index === 0}
                    style={arrowButtonStyle}
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveRow(index, 'down')}
                    disabled={index === items.length - 1}
                    style={arrowButtonStyle}
                  >
                    ▼
                  </button>
                </div>
              </td>

              {/* 項目名（品目検索） */}
              <td style={{ padding: '4px', position: 'relative' }}>
                <input
                  ref={(el) => { inputRefs.current[`${index}-item_name`] = el; }}
                  type="text"
                  value={searchText[index] !== undefined ? searchText[index] : item.item_name}
                  onChange={(e) => {
                    setSearchText(prev => ({ ...prev, [index]: e.target.value }));
                    setShowDropdown(index);
                    updateItem(index, 'item_name', e.target.value);
                  }}
                  onFocus={() => setShowDropdown(index)}
                  onKeyDown={(e) => handleKeyDown(e, index, 'item_name')}
                  style={cellInputStyle}
                />
                {showDropdown === index && filteredItems(index).length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}>
                    {filteredItems(index).map((masterItem: any) => (
                      <div
                        key={masterItem.id}
                        onClick={() => handleItemSelect(index, masterItem)}
                        style={{
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          fontSize: '12px',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                      >
                        {masterItem.name}
                        {masterItem.code && <span style={{ color: '#999', marginLeft: '8px' }}>({masterItem.code})</span>}
                      </div>
                    ))}
                  </div>
                )}
              </td>

              {/* 仕様 */}
              <td style={{ padding: '4px' }}>
                <input
                  ref={(el) => { inputRefs.current[`${index}-specification`] = el; }}
                  type="text"
                  value={item.specification}
                  onChange={(e) => updateItem(index, 'specification', e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index, 'specification')}
                  style={cellInputStyle}
                />
              </td>

              {/* 数量 */}
              <td style={{ padding: '4px' }}>
                <input
                  ref={(el) => { inputRefs.current[`${index}-quantity`] = el; }}
                  type="number"
                  value={item.quantity || 0}
                  onChange={(e) => updateItem(index, 'quantity', Number(e.target.value) || 0)}
                  onKeyDown={(e) => handleKeyDown(e, index, 'quantity')}
                  style={{ ...cellInputStyle, textAlign: 'right' }}
                  min={0}
                  step={1}
                />
              </td>

              {/* 単位 */}
              <td style={{ padding: '4px' }}>
                <input
                  ref={(el) => { inputRefs.current[`${index}-unit`] = el; }}
                  type="text"
                  value={item.unit}
                  onChange={(e) => updateItem(index, 'unit', e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index, 'unit')}
                  style={cellInputStyle}
                />
              </td>

              {/* 単価 */}
              <td style={{ padding: '4px' }}>
                <input
                  ref={(el) => { inputRefs.current[`${index}-unit_price`] = el; }}
                  type="number"
                  value={item.unit_price || 0}
                  onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value) || 0)}
                  onKeyDown={(e) => handleKeyDown(e, index, 'unit_price')}
                  style={{ ...cellInputStyle, textAlign: 'right' }}
                  min={0}
                  step={100}
                />
              </td>

              {/* 金額（自動計算・表示のみ） */}
              <td style={{ padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                ¥{item.amount.toLocaleString()}
              </td>

              {/* 備考 */}
              <td style={{ padding: '4px' }}>
                <input
                  ref={(el) => { inputRefs.current[`${index}-notes`] = el; }}
                  type="text"
                  value={item.notes}
                  onChange={(e) => updateItem(index, 'notes', e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index, 'notes')}
                  style={cellInputStyle}
                />
              </td>

              {/* 削除 */}
              <td style={{ padding: '4px', textAlign: 'center' }}>
                <button
                  onClick={() => removeRow(index)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#d9534f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '11px',
                  }}
                >
                  削除
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 行追加ボタン */}
      <div style={{ marginTop: '10px' }}>
        <button
          onClick={addRow}
          style={{
            padding: '8px 16px',
            backgroundColor: '#5cb85c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          + 行追加
        </button>
      </div>
    </div>
  );
};

const cellInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  border: '1px solid #ddd',
  borderRadius: '3px',
  fontSize: '12px',
  boxSizing: 'border-box',
};

const arrowButtonStyle: React.CSSProperties = {
  padding: '2px 4px',
  fontSize: '8px',
  border: '1px solid #ccc',
  borderRadius: '2px',
  backgroundColor: 'white',
  cursor: 'pointer',
  lineHeight: '1',
};

export default ItemEditor;
